// The OTA release history: every uploaded bundle, newest first, with a LIVE badge
// on the one currently served and a one-tap "Set live" that points the release at
// any bundle (forward or back — the rollback lever). The live pointer's row gets a
// "Verify" shortcut instead. Selecting deliberately keeps the saved gate state.
import { cn } from '../../../lib/cn';
import { Pill, timeAgo } from '../parts';
import { shortHash } from './fields';
import type { OtaAdmin } from './useOtaAdmin';

export function OtaHistoryList({ admin }: { admin: OtaAdmin }) {
  const { available, stored, promote, promotingVersion, verify, verifyState } = admin;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-bold text-ink">Release history</h3>
        <span className="text-[11px] text-ink3">{available.length} uploaded</span>
      </div>
      <ul className="space-y-2">
        {available.map((b) => {
          const isPointer = stored?.version === b.version;
          const isServing = isPointer && !!stored?.enabled;
          const promoting = promotingVersion === b.version;
          return (
            <li
              key={b.version}
              className={cn(
                'flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5',
                isServing
                  ? 'border-accent/30 bg-accent-wash'
                  : isPointer
                    ? 'border-black/10 bg-wash'
                    : 'border-black/5 bg-wash'
              )}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="num text-[14px] font-bold text-ink">v{b.version}</span>
                  {isServing && <Pill tone="accent">LIVE</Pill>}
                  {isPointer && !isServing && <Pill tone="neutral">Target · gate off</Pill>}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-ink3">
                  uploaded {timeAgo(b.at)}
                  {b.minNativeBuild != null ? ` · build ${b.minNativeBuild}+` : ''}
                  {b.checksum ? ` · ${shortHash(b.checksum)}` : ''}
                </div>
              </div>
              {isPointer ? (
                <button
                  type="button"
                  onClick={() => verify(b.version)}
                  disabled={verifyState === 'checking'}
                  className={cn(
                    'tap shrink-0 rounded-lg border border-black/10 bg-card px-3 py-1.5 text-[12px] font-semibold text-ink',
                    verifyState === 'checking' && 'opacity-50'
                  )}
                >
                  Verify
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => promote(b)}
                  disabled={!!promotingVersion}
                  className={cn(
                    'tap shrink-0 rounded-lg bg-night px-3 py-1.5 text-[12px] font-bold text-white',
                    !!promotingVersion && 'opacity-50'
                  )}
                >
                  {promoting ? 'Setting…' : 'Set live'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
